import { MouseEvent as ReactMouseEvent, useMemo, useState } from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, getCitationFilePath, SpeechConfig } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";

export type FeedbackState = "positive" | "negative" | "neutral";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    showDevTools?: boolean;
    onFeedback?: (index: number, feedback: FeedbackState) => void;
    feedback?: FeedbackState;
}

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    showDevTools,
    onFeedback,
    feedback
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const dp = answer.context?.data_points;
    const hasSupportingContent = Boolean(dp && ((dp.text?.length ?? 0) > 0 || (dp.images?.length ?? 0) > 0));
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming), [answer, isStreaming]);
    const { t } = useTranslation();
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const conversationId = typeof answer.session_state === "string" ? answer.session_state : null;
    // Event delegation for inline citations. renderToStaticMarkup + DOMPurify + ReactMarkdown
    // strip any React onClick handlers, so renderCitation emits data-citation-path instead;
    // we resolve it here on click.
    const handleAnswerClick = (event: ReactMouseEvent<HTMLDivElement>) => {
        const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-citation-path]");
        if (!anchor) return;
        const path = anchor.getAttribute("data-citation-path");
        if (!path) return;
        event.preventDefault();
        onCitationClicked(path);
    };
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = sanitizedAnswerHtml;
        tempElement.querySelectorAll("sup").forEach(node => node.remove());
        tempElement.querySelectorAll(".citationStepBadge").forEach(node => node.remove());
        const textToCopy = tempElement.textContent ?? "";

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        {showDevTools && !!answer.context.thoughts?.length && !isStreaming && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "Lightbulb" }}
                                title={t("tooltips.showThoughtProcess")}
                                ariaLabel={t("tooltips.showThoughtProcess")}
                                onClick={() => onThoughtProcessClicked()}
                            />
                        )}
                                            {hasSupportingContent && !isStreaming && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "ClipboardList" }}
                                title={t("tooltips.showSupportingContent")}
                                ariaLabel={t("tooltips.showSupportingContent")}
                                onClick={() => onSupportingContentClicked()}
                            />
                        )}
                        {/* Like/dislike feedback buttons removed
                        {onFeedback && !isStreaming && (
                            <>
                                <IconButton
                                    style={{ color: feedback === "positive" ? "darkgreen" : "slategray" }}
                                    iconProps={{ iconName: "Like" }}
                                    title="Helpful"
                                    ariaLabel="Mark as helpful"
                                    onClick={() => onFeedback(index, feedback === "positive" ? "neutral" : "positive")}
                                />
                                <IconButton
                                    style={{ color: feedback === "negative" ? "darkred" : "slategray" }}
                                    iconProps={{ iconName: "Dislike" }}
                                    title="Not helpful"
                                    ariaLabel="Mark as not helpful"
                                    onClick={() => onFeedback(index, feedback === "negative" ? "neutral" : "negative")}
                                />
                            </>
                        )} */}
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText} onClick={handleAnswerClick}>
                    <ReactMarkdown
                        children={sanitizedAnswerHtml}
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            // Force any external (http/https) link — including web citation badges
                            // and plain markdown links the model emits — to open in a new tab.
                            // Doc-citation anchors carry data-citation-path but no href and so
                            // pass through unchanged, preserving the click-delegation handler.
                            a: ({ href, children, ...rest }) => {
                                const isExternal = typeof href === "string" && /^https?:\/\//i.test(href);
                                return (
                                    <a
                                        href={href}
                                        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                        {...rest}
                                    >
                                        {children}
                                    </a>
                                );
                            }
                        }}
                    />
                </div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && !isStreaming && (
                <Stack.Item>
                    <div className={styles.citationGroupList}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {(() => {
                            // Group doc citations by filename; web citations each render as their own row.
                            // Each badge shows the hierarchical index (e.g. "1.2") that matches the
                            // inline superscript exactly, so users can jump from sup to source visually.
                            const docGroups = new Map<string, { index: string; page?: string; reference: string }[]>();
                            const webEntries: { index: string; reference: string; title: string }[] = [];
                            for (const c of parsedAnswer.citations) {
                                if (c.isWeb) {
                                    const webEntry = answer.context.data_points.external_results_metadata?.find(w => w.url === c.reference);
                                    const titleOrUrl = webEntry?.title?.trim() ? webEntry.title : c.reference;
                                    webEntries.push({ index: c.index, reference: c.reference, title: titleOrUrl });
                                } else {
                                    const [file, hashPart] = c.reference.split("#");
                                    const page = hashPart?.startsWith("page=") ? hashPart.substring(5) : undefined;
                                    const existing = docGroups.get(file) ?? [];
                                    existing.push({ index: c.index, page, reference: c.reference });
                                    docGroups.set(file, existing);
                                }
                            }
                            return (
                                <>
                                    {Array.from(docGroups.entries()).map(([file, items]) => (
                                        <div key={file} className={styles.citationGroupRow}>
                                            <span className={styles.citationGroupFile} title={file}>
                                                {file}
                                            </span>
                                            <span className={styles.citationGroupPages}>
                                                {items.map(c => (
                                                    <a
                                                        key={`${c.reference}-${c.index}`}
                                                        className={styles.citationPageBadge}
                                                        title={c.page ? `${file} — page ${c.page}` : file}
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            onCitationClicked(getCitationFilePath(c.reference, conversationId));
                                                        }}
                                                    >
                                                        {c.page ? `${c.index} (p.${c.page})` : c.index}
                                                    </a>
                                                ))}
                                            </span>
                                        </div>
                                    ))}
                                    {webEntries.map(w => (
                                        <div key={`${w.reference}-${w.index}`} className={styles.citationGroupRow}>
                                            <span className={styles.citationGroupPages}>
                                                <a
                                                    className={styles.citationPageBadge}
                                                    href={w.reference}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={w.reference}
                                                >
                                                    {w.index}
                                                </a>
                                            </span>
                                            <a
                                                className={styles.citationWebLink}
                                                href={w.reference}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={w.reference}
                                            >
                                                {w.title}
                                            </a>
                                        </div>
                                    ))}
                                </>
                            );
                        })()}
                    </div>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
